import * as React from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import { LucideEye, LucideLoader2, LucideTriangleAlert } from 'lucide-react';
import { useRoute } from 'wouter';

import { $selectedChatId } from '../_state/chatConversation.state';
import {
  $isSharedChatView,
  $sharedChatQuery,
  $sharedChatToken,
} from '../_state/sharedChat.state';
import { useTranslations } from '../i18n/useTranslations';

import { useAssistantSidebar } from '~/app/contexts/RightSidebarContext';

//
// ⚛️ ⚛️ ⚛️ SHARED CHAT ROUTE ⚛️ ⚛️ ⚛️
//
export const SharedChatRoute = () => {
  const t = useTranslations();
  const [match, params] = useRoute('/assistant/share/:token');

  // Atoms
  const { data, isLoading, error } = useAtomValue($sharedChatQuery);
  const setSelectedChatId = useSetAtom($selectedChatId);
  const setSharedChatToken = useSetAtom($sharedChatToken);
  const setIsSharedChatView = useSetAtom($isSharedChatView);

  // Data hooks
  const { openAssistant } = useAssistantSidebar();

  // Effects
  /** Sets the shared chat token when the route matches and cleans up on unmount */
  React.useEffect(() => {
    if (match && params?.token) {
      setSharedChatToken(params.token);
    }
    return () => {
      setSharedChatToken(null);
      setIsSharedChatView(false);
    };
  }, [match, params?.token, setSharedChatToken, setIsSharedChatView]);

  /** Opens the assistant sidebar when shared chat data is loaded */
  React.useEffect(() => {
    if (data?.chat) {
      setSelectedChatId(data.chat.id);
      setIsSharedChatView(true);
      openAssistant();
    }
  }, [data?.chat, setSelectedChatId, setIsSharedChatView, openAssistant]);

  if (!match) return null;

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-neutral/60">
        <LucideLoader2 className="size-5 animate-spin" />
        <span>{t.sidebar.loading}</span>
      </div>
    );
  }

  if (error || !data?.chat) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <LucideTriangleAlert className="size-8 text-failure" />
        <p className="text-sm text-neutral/70">{t.chat.sharedLinkInvalid}</p>
      </div>
    );
  }

  return (
    <div className="@SharedChatRoute flex h-full items-center justify-center gap-2 text-neutral/60">
      <LucideEye className="size-5" />
      <span>{t.chat.readOnlyMessage}</span>
    </div>
  );
};
